import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  PERMISSIONS,
  PLATFORM_MODULES,
  RECORD_TYPES,
  VerificationRequestPolicy,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IVerticalTabProps } from "@miplatform/types";
import { getUpdatedFields } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAlertCircleOutline, mdiChevronDown } from "@mdi/js";
import { usePermissions, useTrades } from "@mibuildweb/hooks";
import { Typography, useTheme } from "@mui/material";
import { VerticalTabsLayout } from "../../../../components";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ConfigMenu } from "../../components";
import {
  IEmailTemplateForm,
  IEmailTemplateFormItem,
  IOrderTemplateForm,
} from "../ConfigOrderTemplates.types";
import {
  configEmailTemplateSchema,
  configOrderTemplateSchema,
} from "../schemas/ConfigOrderTemplate.schema";
import { OrderAccountBillingTabView } from "../tabs/OrderAccountBillingTabView";
import { OrderCreationFieldsTabView } from "../tabs/OrderCreationFieldsTabView";
import { OrderDepartmentTabView } from "../tabs/OrderDepartmentsTabView";
import { OrderDetailsTabView } from "../tabs/OrderDetailsTabView";
import { OrderEmailTemplateTabView } from "../tabs/OrderEmailTemplateTabView";
import { OrderIssueTemplatesTabView } from "../tabs/OrderIssueTemplatesTabView";
import { OrderSchedulingRulesTabView } from "../tabs/OrderSchedulingRules";
import { OrderWorkOrderTemplatesTabView } from "../tabs/OrderWorkOrderTemplatesTabView";
import { OrderWorkflowTimingTabView } from "../tabs/OrderWorkflowTimingTabView";
import { useOrderTemplateDetailsViewModel } from "./useOrderTemplateDetailsViewModel";

export function ConfigOrderTemplateDetails() {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { params, goBack } = useRouter();
  const { id: orderTemplateId } = params;
  const theme = useTheme();

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.ORDER_TEMPLATE.UPDATE);

  const form = useForm<IOrderTemplateForm>({
    mode: "onChange",
    defaultValues: defaultOrderTemplateData,
    resolver: zodResolver(configOrderTemplateSchema),
  });

  const {
    formState: { isValid, isDirty, dirtyFields },
    getValues,
    setValue,
    reset,
    watch,
  } = form;

  const {
    fields,
    isActive,
    id,
    name,
    module,
    departments,
    requiresApproval,
    approvalLeadDays,
    approvalLeadTime,
    requiresConfirmation,
    confirmationLeadDays,
    confirmationLeadTime,
    expectedDurationInHours,
    scheduleTimeThreshold,
    allowableScheduleLimit,
    maxTimesPutOnHold,
    codPaymentAllowed,
    invoicePaymentAllowed,
    isPrebill,
    fulfillmentRequiredForInvoicing,
    issueTemplates,
    lockRescheduleThreshold,
    fulfillmentSequence,
    hasCameraLine,
  } = watch();

  const emailForm = useForm<IEmailTemplateForm>({
    mode: "onChange",
    defaultValues: defaultEmailTemplateData,
    resolver: zodResolver(configEmailTemplateSchema),
  });

  const {
    formState: { isDirty: isEmailDirty, isValid: isEmailValid },
    getValues: getEmailValues,
    reset: emailReset,
    watch: emailWatch,
  } = emailForm;

  const { emailTemplates } = emailWatch();

  const {
    isLoading,
    getOrderTemplateDetails,
    updateDraftStatus,
    getEmailTemplatesByOrderTemplateId,
    handleCreateOrderTemplate,
    handleUpdateOrderTemplate,
  } = useOrderTemplateDetailsViewModel({
    reset,
    emailReset,
    isOrderTemplateDirty: isDirty,
    isEmailTemplateDirty: isEmailDirty,
  });

  const { getTrades, tradeOptions: trades } = useTrades();

  useEffect(() => {
    if (orderTemplateId) {
      getOrderTemplateDetails(orderTemplateId);
      getEmailTemplatesByOrderTemplateId(orderTemplateId, {
        onSuccess: (emailTemplateArray: IEmailTemplateFormItem[]) => {
          if (emailTemplates.length > 0) {
            emailReset({ emailTemplates: emailTemplateArray });
          }
        },
      });
    }
  }, [orderTemplateId]);

  useEffect(() => {
    getTrades();
  }, []);

  // NOTE: defaults to Plumbing for QxP Parity as a read-only
  useEffect(() => {
    if (trades.length === 0) {
      return;
    }
    const plumbing = trades.find((t) => t.label === "Plumbing");
    if (plumbing && typeof plumbing.value === "string") {
      setValue("tradeId", plumbing.value);
    }
  }, [trades]);

  const isApprovalIncomplete =
    requiresApproval && (!approvalLeadDays || !approvalLeadTime);
  const isConfirmationIncomplete =
    requiresConfirmation && (!confirmationLeadDays || !confirmationLeadTime);

  const orderTemplateActions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        reset();
        goBack();
      },
    },
    submit: {
      label: `Save ${ENTITY_NAMES.ORDER_TEMPLATE}`,
      onClick: () => {
        setOpenConfirmModal(true);
      },
      disabled:
        !isValid ||
        !isEmailValid ||
        (!isDirty && !isEmailDirty) ||
        isLoading ||
        isApprovalIncomplete ||
        isConfirmationIncomplete,
    },
  };

  const isDepartmentsSelected = departments?.length > 0;
  const isVisibleOrRequired = fields?.some((f) => {
    if (f.fieldName === "requesterId") {
      return f.required;
    }
    return f.visible || f.required;
  });
  const isWorkFlowFilled =
    approvalLeadDays ||
    approvalLeadTime ||
    confirmationLeadDays ||
    confirmationLeadTime;
  const isSchedulingFilled =
    expectedDurationInHours ||
    maxTimesPutOnHold ||
    scheduleTimeThreshold ||
    allowableScheduleLimit ||
    lockRescheduleThreshold ||
    fulfillmentSequence;
  const isRecordablesSelected =
    issueTemplates?.filter((it) => it.recordType === RECORD_TYPES.RECORDABLE)
      .length > 0;
  const isWarrantyItemsSelected =
    issueTemplates?.filter((it) => it.recordType === RECORD_TYPES.SERVICE_ITEM)
      .length > 0;
  const isCameraLinesSelected =
    issueTemplates?.filter((it) => it.recordType === RECORD_TYPES.CAMERA_LINE)
      .length > 0;
  const isAccountBillingFilled =
    (codPaymentAllowed && invoicePaymentAllowed) ||
    isPrebill ||
    fulfillmentRequiredForInvoicing;
  const isEmailFilled = emailTemplates?.some((et) => et.emailType);

  const isEmailTemplateTabDisabled = module === PLATFORM_MODULES.MI_ACCOUNTING;

  const tabs = useMemo<IVerticalTabProps<IOrderTemplateForm>[]>(
    () => [
      {
        label: "Order Details",
        value: "order details",
        controlledFormViewComponent: OrderDetailsTabView,
        isComplete: isValid,
        hasErrors: !isValid,
        extraTabProps: { isReadOnly, emailForm },
      },
      {
        label: "Department",
        value: "department",
        controlledFormViewComponent: OrderDepartmentTabView,
        isComplete: isDepartmentsSelected,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Order Creation Fields",
        value: "order creation fields",
        controlledFormViewComponent: OrderCreationFieldsTabView,
        isComplete: isVisibleOrRequired,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Workflow Timing",
        value: "workflow timing",
        disabled: module === PLATFORM_MODULES.MI_DISPATCH,
        controlledFormViewComponent: OrderWorkflowTimingTabView,
        isComplete:
          requiresApproval || requiresConfirmation
            ? !isApprovalIncomplete && !isConfirmationIncomplete
            : !!isWorkFlowFilled,
        hasErrors: isApprovalIncomplete || isConfirmationIncomplete,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Scheduling Rules",
        value: "scheduling rules",
        controlledFormViewComponent: OrderSchedulingRulesTabView,
        isComplete: !!isSchedulingFilled,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Recordables",
        value: "recordables",
        isComplete: isRecordablesSelected,
        controlledFormViewComponent: OrderIssueTemplatesTabView,
        extraTabProps: { isReadOnly, view: "recordables" },
      },
      {
        label: "Camera Lines",
        value: "camera lines",
        disabled: !hasCameraLine,
        isComplete: isCameraLinesSelected,
        controlledFormViewComponent: OrderIssueTemplatesTabView,
        extraTabProps: {
          isReadOnly: isReadOnly || !hasCameraLine,
          view: "cameraLines",
        },
      },
      {
        label: "Warranty Items",
        value: "warranty items",
        isComplete: isWarrantyItemsSelected,
        controlledFormViewComponent: OrderIssueTemplatesTabView,
        extraTabProps: { isReadOnly, view: "warrantyItems" },
      },
      {
        label: ENTITY_NAMES.WORK_ORDER_TEMPLATES,
        value: "work order templates",
        controlledFormViewComponent: OrderWorkOrderTemplatesTabView,
      },
      {
        label: ENTITY_NAMES.EMAIL_TEMPLATE,
        value: "email template",
        controlledFormViewComponent: OrderEmailTemplateTabView,
        disabled: isEmailTemplateTabDisabled,
        isComplete: isEmailFilled,
        extraTabProps: {
          isReadOnly,
          emailForm,
          isDisabled: isEmailTemplateTabDisabled,
          module,
        },
      },
      {
        label: "Accounting & Billing",
        value: "accounting & billing",
        controlledFormViewComponent: OrderAccountBillingTabView,
        isComplete: isAccountBillingFilled,
        extraTabProps: { isReadOnly },
      },
    ],
    [
      isValid,
      isDepartmentsSelected,
      isVisibleOrRequired,
      isWorkFlowFilled,
      isSchedulingFilled,
      isApprovalIncomplete,
      isConfirmationIncomplete,
      isRecordablesSelected,
      isCameraLinesSelected,
      isWarrantyItemsSelected,
      isEmailFilled,
      isAccountBillingFilled,
      module,
      isReadOnly,
      hasCameraLine,
    ]
  );

  const handleSubmitOrderTemplate = (isActive: boolean) => {
    const data = { ...getValues(), isActive };
    const emailData = getEmailValues();

    if (orderTemplateId) {
      const updatedFields = getUpdatedFields(
        dirtyFields,
        data
      ) as Partial<IOrderTemplateForm>;

      if (updatedFields.hasCameraLine === false) {
        updatedFields.issueTemplates = data.issueTemplates.filter(
          (it) => it.recordType !== RECORD_TYPES.CAMERA_LINE
        );
      }

      handleUpdateOrderTemplate(updatedFields, emailData, orderTemplateId);
    } else {
      handleCreateOrderTemplate(data, emailData);
    }
    setOpenConfirmModal(false);
  };

  const renderPageTitle = () => {
    const action = orderTemplateId ? "Edit" : "Create";
    const orderTemplateLabel = name || `New ${ENTITY_NAMES.ORDER_TEMPLATE}`;
    return (
      <Typography variant="HD4">
        {action} {ENTITY_NAMES.ORDER_TEMPLATE}:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {orderTemplateLabel}
        </Typography>
      </Typography>
    );
  };

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={orderTemplateActions}
      loading={isLoading}
      titleActionComponent={
        id ? (
          <ConfigMenu
            data={[
              {
                label: isActive ? "Draft" : "Activate",
                action: () => {
                  updateDraftStatus(id, !isActive);
                },
              },
            ]}
            optionalElement={{
              size: "medium",
              title: isActive ? "Status: Active" : "Status: Draft",
              color: isActive ? "success" : "orange",
              endIcon: { path: mdiChevronDown },
              sx: { width: 85 },
            }}
          />
        ) : undefined
      }
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
      <MiModal
        title={`You Are Saving Your ${ENTITY_NAMES.ORDER_TEMPLATE}`}
        description={
          <>
            You are saving{" "}
            <span
              style={{
                color: theme.palette.primary.main,
                fontWeight: theme.fontWeight.semiBold,
              }}
            >
              {name}
            </span>
            . Would you like to activate this{" "}
            {ENTITY_NAMES.ORDER_TEMPLATE.toLowerCase()} to be used in your org?
          </>
        }
        titleIcon={mdiAlertCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={openConfirmModal}
        closeModal={() => setOpenConfirmModal(false)}
        actions={[
          {
            name: "Save As Draft",
            color: BUTTON_COLORS.ORANGE,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => handleSubmitOrderTemplate(false),
          },
          {
            name: isActive ? "Save Type" : "Activate Type",
            color: BUTTON_COLORS.SUCCESS,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => handleSubmitOrderTemplate(true),
          },
        ]}
      />
    </ConfigPageLayout>
  );
}

const defaultOrderTemplateData: IOrderTemplateForm = {
  name: "",
  shortName: "",
  code: "",
  abbreviation: "",
  description: "",
  color: "",
  homePhases: [],
  tradeId: "",
  module: "",
  isActive: false,
  departments: [],
  requiresApproval: false,
  approvalLeadDays: null,
  approvalLeadTime: null,
  requiresConfirmation: false,
  confirmationLeadDays: null,
  confirmationLeadTime: null,
  expectedDurationInHours: null,
  maxTimesPutOnHold: null,
  scheduleTimeThreshold: null,
  allowableScheduleLimit: null,
  codPaymentAllowed: false,
  invoicePaymentAllowed: false,
  isPrebill: false,
  fulfillmentRequiredForInvoicing: false,
  lockRescheduleThreshold: null,
  fulfillmentSequence: null,
  workOrderTemplates: [],
  issueTemplates: [],
  assignmentTemplates: [],
  hasCameraLine: false,
  fields: [
    {
      fieldName: "accountId",
      displayName: "Customer",
      section: "Customer Info",
      displayOrder: 0,
      sectionOrder: 1,
      visible: false,
      required: false,
      componentType: "customerSelect",
    },
    {
      fieldName: "documents",
      displayName: "Browse Files",
      section: "Additional Job Info",
      displayOrder: 1,
      sectionOrder: 3,
      visible: false,
      required: false,
      componentType: "filePicker",
    },
    {
      fieldName: "issue",
      displayName: "Task",
      section: "Issue",
      displayOrder: 0,
      sectionOrder: 0,
      visible: false,
      required: false,
      componentType: "orderItemsCardSelect",
    },
    {
      fieldName: "invoiceName",
      displayName: "EPO - Invoice Name",
      section: "EPO",
      displayOrder: 0,
      sectionOrder: 0,
      visible: false,
      required: false,
      componentType: "textArea",
    },
    {
      fieldName: "notes",
      displayName: "General Notes",
      section: "Additional Job Info",
      displayOrder: 0,
      sectionOrder: 3,
      visible: false,
      required: false,
      componentType: "textArea",
    },
    {
      fieldName: "orderDetails",
      displayName: "Order Details",
      section: "Order Details",
      displayOrder: 0,
      sectionOrder: 0,
      visible: false,
      required: false,
      componentType: "orderDetailsCard",
    },
    {
      fieldName: "recipientId",
      displayName: "Point of Contact",
      section: "Point of Contact",
      displayOrder: 0,
      sectionOrder: 0,
      visible: false,
      required: false,
      componentType: "homeContactCardSelect",
    },
    {
      fieldName: "requestedDate",
      displayName: "Requested Date",
      section: "Requested Date",
      displayOrder: 0,
      sectionOrder: 0,
      visible: false,
      required: false,
      componentType: "requestedDateCard",
    },
    {
      fieldName: "requesterId",
      displayName: "Reported By",
      section: "Reported By",
      displayOrder: 0,
      sectionOrder: 1,
      visible: true,
      required: false,
      componentType: "homeContactAutocomplete",
    },
  ],
  verificationRequestPolicy: VerificationRequestPolicy.CanRequestVerification,
  requiresCustomerSignature: false,
};

const defaultEmailTemplateData: IEmailTemplateForm = {
  emailTemplates: [
    {
      emailType: "",
      replyTo: "",
      to: [],
      cc: [],
    },
  ],
};
